"use client";

import { Box } from "@mui/material";
import { icons, LucideIcon } from "lucide-react";
import Link from "next/link";

type IconViewportSettings = {
    size?: string | number;
    box_size?: string | number;
    rotation?: string | number;
    visible?: boolean;
};

type IconLCSettings = {
    label?: string | null;
    name: string;
    color?: string | null;
    stroke_width?: string | number;
    background_color?: string | null;
    border_color?: string | null;
    border_width?: string | number;
    border_radius?: string | number;
    link?: string | null;
    aria_label?: string | null;
    align?: 'flex-start' | 'center' | 'flex-end';
    mobile?: IconViewportSettings;
    tablet?: IconViewportSettings;
    desktop?: IconViewportSettings;
};

const iconMap = Object.fromEntries(Object.entries(icons).map(([name, Icon]) => [toKebabCase(name), Icon])) as Partial<Record<string, LucideIcon>>;

export default function IconLC({ content, disableLinks }: {
    content: { settings: IconLCSettings; data: null };
    disableLinks?: boolean;
}) {
    const settings = content.settings;
    const Icon = iconMap[settings?.name];
    if (!Icon) return null;

    const mobile = normalizeViewport(settings.mobile);
    const tablet = normalizeViewport(settings.tablet ?? settings.mobile);
    const desktop = normalizeViewport(settings.desktop ?? settings.tablet ?? settings.mobile);

    const frame = (
        <Box
            component="span"
            role={settings.aria_label ? 'img' : undefined}
            aria-label={settings.aria_label ?? undefined}
            aria-hidden={settings.aria_label ? undefined : true}
            sx={{
                display: {
                    xs: mobile.visible ? 'inline-flex' : 'none',
                    sm2: tablet.visible ? 'inline-flex' : 'none',
                    lg: desktop.visible ? 'inline-flex' : 'none',
                },
                width: { xs: mobile.boxSize, sm2: tablet.boxSize, lg: desktop.boxSize },
                height: { xs: mobile.boxSize, sm2: tablet.boxSize, lg: desktop.boxSize },
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                color: settings.color ?? 'currentColor',
                bgcolor: settings.background_color ?? 'transparent',
                border: `${Number(settings.border_width ?? 0)}px solid ${settings.border_color ?? 'transparent'}`,
                borderRadius: `${settings.border_radius ?? 0}px`,
                textDecoration: 'none',
            }}
        >
            <Box
                component="span"
                sx={{
                    display: 'inline-flex',
                    width: { xs: mobile.iconSize, sm2: tablet.iconSize, lg: desktop.iconSize },
                    height: { xs: mobile.iconSize, sm2: tablet.iconSize, lg: desktop.iconSize },
                    transform: {
                        xs: `rotate(${mobile.rotation}deg)`,
                        sm2: `rotate(${tablet.rotation}deg)`,
                        lg: `rotate(${desktop.rotation}deg)`,
                    },
                }}
            >
                <Icon
                    size="100%"
                    color={settings.color ?? 'currentColor'}
                    strokeWidth={Number(settings.stroke_width ?? 2)}
                    // The global .lucide rule must not override the data-driven stroke colour.
                    style={{ color: 'inherit' }}
                />
            </Box>
        </Box>
    );

    return (
        <Box sx={{ display: 'flex', justifyContent: settings.align ?? 'center' }}>
            {settings.link && !disableLinks ? <Link href={settings.link} style={{ display: 'inline-flex' }}>{frame}</Link> : frame}
        </Box>
    );
}

function normalizeViewport(viewport?: IconViewportSettings) {
    return {
        iconSize: `${viewport?.size ?? 24}px`,
        boxSize: `${viewport?.box_size ?? 44}px`,
        rotation: Number(viewport?.rotation ?? 0),
        visible: viewport?.visible !== false,
    };
}

function toKebabCase(value: string) {
    return value
        .replace(/([A-Z]+)([A-Z][a-z])/g, '$1-$2')
        .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
        .toLowerCase();
}
